/**
 * Crewmates' positions over server time. Each presence message is a sample; walks play back from
 * their start time. The world shows crewmates about 220 ms in the past, interpolated between the two
 * samples around that moment, so network jitter never shows as stutter.
 */
import type { Vec3 } from '@bramvasterd/terrella-protocol';

import { easeInOut } from '@core/math';
import { vec, vecCopy, vecNormalize, vecSlerp, type Vec } from '@core/quat';

export const DISPLAY_DELAY_MS = 220;
const KEEP_SAMPLES = 24;
/** A crewmate who stopped sending is shown still after this long. */
const STILL_AFTER_MS = 600;

export interface PresenceSample {
  time: number;
  p: Vec;
  h: Vec;
  moving: boolean;
}

export interface CrewmateWalk {
  from: Vec;
  to: Vec;
  start: number;
  duration: number;
}

export interface Crewmate {
  id: string;
  samples: PresenceSample[];
  walk: CrewmateWalk | null;
  /** Server time of the last message. */
  lastSeen: number;
}

export function createCrewmate(id: string): Crewmate {
  return { id, samples: [], walk: null, lastSeen: 0 };
}

const toVec = (v: Vec3): Vec => vecNormalize(vec(v[0], v[1], v[2]));

export function pushSample(mate: Crewmate, time: number, p: Vec3, h: Vec3, moving: boolean): void {
  const sample: PresenceSample = { time, p: toVec(p), h: toVec(h), moving };
  const samples = mate.samples;
  let i = samples.length;
  while (i > 0 && (samples[i - 1]?.time ?? 0) > time) i--;
  samples.splice(i, 0, sample);
  if (samples.length > KEEP_SAMPLES) samples.splice(0, samples.length - KEEP_SAMPLES);
  mate.lastSeen = Math.max(mate.lastSeen, time);
  if (mate.walk && time > mate.walk.start + mate.walk.duration) mate.walk = null;
}

export function startWalk(mate: Crewmate, to: Vec3, start: number, duration: number): void {
  const from = vec();
  const heading = vec();
  positionAt(mate, start, from, heading);
  mate.walk = { from, to: toVec(to), start, duration: Math.max(1, duration) };
  mate.lastSeen = Math.max(mate.lastSeen, start);
}

/**
 * Where a crewmate is at server time `t`, into `outP` and `outH`. Returns whether they're moving.
 * Before the first sample they stand on it; after the last they stay there.
 */
export function positionAt(mate: Crewmate, t: number, outP: Vec, outH: Vec): boolean {
  const walk = mate.walk;
  if (walk && t >= walk.start) {
    const p = Math.min(1, (t - walk.start) / walk.duration);
    vecNormalize(vecSlerp(outP, walk.from, walk.to, easeInOut(p)));
    const last = mate.samples[mate.samples.length - 1];
    if (last) vecCopy(outH, last.h);
    else headingTowards(outH, walk.from, walk.to);
    if (p < 1) {
      headingTowards(outH, walk.from, walk.to);
      return true;
    }
    if (!last || last.time <= walk.start + walk.duration) return false;
  }
  const samples = mate.samples;
  if (samples.length === 0) {
    outP[0] = 0;
    outP[1] = 1;
    outP[2] = 0;
    outH[0] = 0;
    outH[1] = 0;
    outH[2] = 1;
    return false;
  }
  const first = samples[0];
  const last = samples[samples.length - 1];
  if (!first || !last) return false;
  if (t <= first.time) {
    vecCopy(outP, first.p);
    vecCopy(outH, first.h);
    return first.moving;
  }
  if (t >= last.time) {
    vecCopy(outP, last.p);
    vecCopy(outH, last.h);
    return last.moving && t - last.time < STILL_AFTER_MS;
  }
  let hi = 1;
  while ((samples[hi]?.time ?? Infinity) < t) hi++;
  const a = samples[hi - 1];
  const b = samples[hi];
  if (!a || !b) return false;
  const span = b.time - a.time;
  const k = span > 0 ? (t - a.time) / span : 1;
  vecNormalize(vecSlerp(outP, a.p, b.p, k));
  vecCopy(outH, k < 0.5 ? a.h : b.h);
  return a.moving || b.moving;
}

const tmp = vec();

function headingTowards(out: Vec, from: Vec, to: Vec): void {
  tmp[0] = (to[0] ?? 0) - (from[0] ?? 0);
  tmp[1] = (to[1] ?? 0) - (from[1] ?? 0);
  tmp[2] = (to[2] ?? 0) - (from[2] ?? 0);
  if (Math.hypot(tmp[0], tmp[1], tmp[2]) < 1e-6) return;
  vecCopy(out, vecNormalize(tmp));
}
